import { useMemo } from 'react';

import { STRINGS } from '@/constants/strings';
import type { GuardianCircleData } from '@/features/guardian-player/types';
import { buildGuardianCircle } from '@/features/guardian-player/utils';
import type { GuardianPlayer } from '@/features/guardian/services/get-guardian-players';
import { usePlayerGuardiansQuery } from '@/queries/use-player-guardians-query';
import type { PlayerGuardian } from '@/services/get-player-guardians';

const strings = STRINGS.guardianPlayer.guardianCircle;

export type UseGuardianCircleResult = GuardianCircleData & {
  /** Player's first name, used for the screen title / subtitle / leave copy. */
  firstName: string;
  /** Raw API rows, kept so a tapped row can be routed by its full status/details. */
  guardians: PlayerGuardian[];
  isLoading: boolean;
  isError: boolean;
  /** True while a pull-to-refresh refetch is in flight. */
  isRefetching: boolean;
  refetch: () => void;
};

/**
 * Provides the Guardian Circle view model for a player from
 * GET /api/v1/guardians/me/players/{playerId}/guardians. Returns empty defaults
 * while the request is in flight so the screen can show its loading state.
 */
export function useGuardianCircle(player: GuardianPlayer): UseGuardianCircleResult {
  const firstName = player.firstname?.trim() || strings.fallbackPlayerName;

  const { data, isLoading, isError, isRefetching, refetch } = usePlayerGuardiansQuery(player.id);

  const circle = useMemo(() => buildGuardianCircle(data), [data]);

  return { ...circle, firstName, guardians: data ?? [], isLoading, isError, isRefetching, refetch };
}
